import React from "react";
import { Row, Col } from "reactstrap";
import Select from "react-select";
import { numberWithCommas } from "../../../utils/customFunction";
import CardColComponent from "../../genericCardComponent/CardColComponent";
import moment from "moment";
import { getStyles } from "./utils";

interface Props {
  // Form state
  fund: string;
  folioNo: string;
  fromDate: string;
  toDate: string;

  // Fund Class dropdown (optional so other screens using this form keep working)
  fundClass?: string;
  fundClassOptions?: { value: string; label: string }[];
  onFundClassChange?: (value: string) => void;

  // Data
  allFunds: any[];
  unitholderOptions: any[];

  // Loading states
  folioLoading: boolean;
  unitholderLoading: boolean;
  loading: boolean;

  // Handlers
  onFundChange: (value: string) => void;
  onFolioChange: (value: any) => void;
  onFromDateChange: (value: string) => void;
  onToDateChange: (value: string) => void;
  onSearch: () => void;
  onUnitholderSearchInput: (value: string) => void;

  // Validation
  folioNoErr: string;
  fromDateErr: string;
  toDateErr: string;

  // Styles
  customStyles: any;
  theme: string;

  // Other
  isParticipant: boolean;

  dividend_statement?: boolean;
}

export const UnitHolderSearchForm: React.FC<Props> = ({
  fund,
  folioNo,
  fromDate,
  toDate,
  fundClass,
  fundClassOptions,
  onFundClassChange,
  allFunds,
  unitholderOptions,
  folioLoading,
  unitholderLoading,
  loading,
  onFundChange,
  onFolioChange,
  onFromDateChange,
  onToDateChange,
  onSearch,
  onUnitholderSearchInput,
  folioNoErr,
  fromDateErr,
  toDateErr,
  customStyles,
  theme,
  isParticipant,
  dividend_statement,
}) => {
  const renderFundsDropdown = () => {
    const options = allFunds.map((item: any) => ({
      value: item.symbol_code,
      label:
        item.nature === "Special Managed Account"
          ? `${item.fund_name} (${item.symbol_code}) - SMA`
          : `${item.fund_name} (${item.symbol_code})`,
    }));

    return [{ value: "all", label: "All" }, ...options];
  };

  const selectedFundName = React.useMemo(() => {
    if (fund === "all") return { value: "all", label: "All" };
    if (!fund) return null;

    const selectedFund = allFunds.find((item) => item?.symbol_code === fund);
    if (!selectedFund) return null;

    return {
      value: selectedFund.symbol_code,
      label:
        selectedFund.nature === "Special Managed Account"
          ? `${selectedFund.fund_name} (${selectedFund.symbol_code}) - SMA`
          : `${selectedFund.fund_name} (${selectedFund.symbol_code})`,
    };
  }, [allFunds, fund]);

  const check = dividend_statement ? true : folioNo;

  return (
    <Row>
      {!isParticipant && (
        <Col xl={2} lg={3} md={4}>
          <div className="form-group">
            <label>Fund Name</label>
            {folioLoading ? (
              <div className="form-control w-100">
                <i className="fa fa-spinner fa-spin fa-1x"></i>
              </div>
            ) : (
              <Select
                value={selectedFundName}
                onChange={(e) => onFundChange(e?.value)}
                menuPortalTarget={document.body}
                menuShouldBlockScroll={true}
                isClearable={true}
                isSearchable={true}
                options={renderFundsDropdown()}
                styles={customStyles}
              />
            )}
          </div>
        </Col>
      )}

      <Col xl={2} lg={3} md={4}>
        <div className="form-group">
          <label>Enter Folio No.</label>
          <Select
            options={unitholderOptions}
            onChange={onFolioChange}
            onInputChange={onUnitholderSearchInput}
            placeholder="Search by folio number or name..."
            isLoading={unitholderLoading}
            isClearable
            value={
              unitholderOptions.find((opt) => opt.folio_no === folioNo) || null
            }
            noOptionsMessage={() =>
              unitholderLoading
                ? "Searching..."
                : "No unitholders found. Try a different search."
            }
            filterOption={null}
            styles={customStyles}
          />
        </div>
      </Col>

      {fundClassOptions && onFundClassChange && (
        <Col xl={2} lg={3} md={4}>
          <div className="form-group">
            <label>Fund Class</label>
            <Select
              options={fundClassOptions}
              value={fundClassOptions.find((o) => o.value === fundClass) || null}
              onChange={(option: any) =>
                onFundClassChange(option ? option.value : "")
              }
              placeholder="Select Fund Class"
              menuPortalTarget={document.body}
              menuShouldBlockScroll={true}
              isClearable
              styles={customStyles}
            />
          </div>
        </Col>
      )}

      <Col xl={2} lg={3} md={4}>
        <div className="form-group">
          <label>Settlement Date From</label>
          <input
            type="date"
            value={fromDate}
            className={`form-control input_date py-1 ${fromDateErr}`}
            onChange={(e) => onFromDateChange(e.target.value)}
          />
        </div>
      </Col>

      <Col xl={2} lg={3} md={4}>
        <div className="form-group">
          <label>Settlement Date To</label>
          <input
            type="date"
            value={toDate}
            className={`form-control input_date py-1 ${toDateErr}`}
            onChange={(e) => onToDateChange(e.target.value)}
          />
        </div>
      </Col>

      <Col xl={2} lg={3} md={4}>
        <div style={{ marginTop: "5px" }}>
          <button
            className="btn btn-primary py-2 mt-4"
            onClick={onSearch}
            disabled={loading || !allFunds || allFunds.length === 0 || !check}
          >
            {loading ? (
              <>
                <span className="spinner-border login-txt spinner-border-sm" />
                <span className="login-txt"> Loading...</span>
              </>
            ) : (
              <span>Search</span>
            )}
          </button>
        </div>
      </Col>
    </Row>
  );
};

interface ParticipantStatementTableProps {
  participantRows: any[];
  fundName: (code: string) => string;
  CapTxnType: (type: string) => string;
}

export const ParticipantStatementTable: React.FC<
  ParticipantStatementTableProps
> = ({ participantRows, fundName, CapTxnType }) => {
  return (
    <>
      <table
        className="report"
        style={{
          width: "100%",
          color: "black",
          borderCollapse: "collapse",
        }}
      >
        <thead>
          <tr
            style={{
              borderBottom: "2px solid black",
              fontSize: "13px",
              fontWeight: "bold",
            }}
          >
            <th style={{ width: "10%", padding: "8px", textAlign: "left" }}>
              Date
            </th>
            <th style={{ width: "15%", padding: "8px", textAlign: "left" }}>
              TXN ID
            </th>
            <th style={{ width: "15%", padding: "8px", textAlign: "left" }}>
              Type
            </th>
            <th style={{ width: "15%", padding: "8px", textAlign: "left" }}>
              Fund
            </th>
            <th style={{ width: "10%", padding: "8px", textAlign: "right" }}>
              NAV
            </th>
            <th style={{ width: "10%", padding: "8px", textAlign: "right" }}>
              Units
            </th>
            <th style={{ width: "12%", padding: "8px", textAlign: "right" }}>
              Amount
            </th>
            <th style={{ width: "13%", padding: "8px", textAlign: "right" }}>
              Closing Balance
            </th>
          </tr>
        </thead>
      </table>

      {participantRows?.map((unitHolder, index) => (
        <div key={index} style={{ marginTop: "20px" }}>
          {/* Participant Header */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "10px",
              borderTop: "1px solid #ddd",
              paddingTop: "10px",
            }}
          >
            <div
              style={{ fontSize: "13px", fontWeight: "bold", color: "black" }}
            >
              {fundName(unitHolder?.fund_code)
                ? `${fundName(unitHolder?.fund_code)} (${
                    unitHolder?.fund_code
                  })`
                : ""}
            </div>
            <div style={{ fontSize: "12px", color: "black" }}>
              <b>Opening Balance:</b>{" "}
              {numberWithCommas(
                parseFloat(unitHolder?.opening_balance || 0).toFixed(4),
              )}
            </div>
          </div>

          {/* Transaction Table */}
          <table
            className="report"
            style={{
              width: "100%",
              color: "black",
              borderCollapse: "collapse",
              fontSize: "12px",
            }}
          >
            <tbody>
              {unitHolder?.subFunds?.map((item, idx) => (
                <tr key={idx} style={{ borderBottom: "1px solid #eee" }}>
                  <td style={{ width: "10%", padding: "6px" }}>
                    {new Date(item?.date).toLocaleDateString("en-GB")}
                  </td>
                  <td style={{ width: "15%", padding: "6px" }}>
                    {item?.txn_id}
                  </td>
                  <td style={{ width: "15%", padding: "6px" }}>
                    {item?.txnr_type === "changeofallocation" &&
                    item?.allocation_type &&
                    item?.amount > 0 &&
                    item?.units > 0
                      ? `${item?.txnr_type} (${item?.allocation_type})`
                      : item?.txnr_type}
                  </td>
                  <td style={{ width: "15%", padding: "6px" }}>
                    {item?.fund_code}
                  </td>
                  <td
                    style={{ width: "10%", padding: "6px", textAlign: "right" }}
                  >
                    {numberWithCommas(parseFloat(item?.nav || 0).toFixed(4))}
                  </td>
                  <td
                    style={{ width: "10%", padding: "6px", textAlign: "right" }}
                  >
                    {numberWithCommas(parseFloat(item?.units || 0).toFixed(4))}
                  </td>
                  <td
                    style={{ width: "12%", padding: "6px", textAlign: "right" }}
                  >
                    {numberWithCommas(parseFloat(item?.amount || 0).toFixed(4))}
                  </td>
                  <td
                    style={{ width: "13%", padding: "6px", textAlign: "right" }}
                  >
                    {numberWithCommas(
                      parseFloat(item?.closing_balance || 0).toFixed(4),
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th style={{ width: "10%", padding: "6px" }}>Total</th>
                <th style={{ width: "15%", padding: "6px" }}></th>
                <th style={{ width: "15%", padding: "6px" }}></th>
                <th style={{ width: "15%", padding: "6px" }}></th>
                <th
                  style={{ width: "10%", padding: "6px", textAlign: "right" }}
                ></th>
                <th
                  style={{ width: "10%", padding: "6px", textAlign: "right" }}
                >
                  {numberWithCommas(
                    parseFloat(unitHolder?.total_units || 0).toFixed(4),
                  )}
                </th>
                <th
                  style={{ width: "12%", padding: "6px", textAlign: "right" }}
                >
                  {numberWithCommas(
                    parseFloat(unitHolder?.total_amount || 0).toFixed(4),
                  )}
                </th>
                <th
                  style={{ width: "13%", padding: "6px", textAlign: "right" }}
                ></th>
              </tr>
            </tfoot>
          </table>

          {/* Closing Balance */}
          <div
            style={{
              textAlign: "right",
              marginTop: "8px",
              marginBottom: "10px",
              fontSize: "12px",
              color: "black",
            }}
          >
            <b>Closing Balance:</b>{" "}
            {numberWithCommas(
              parseFloat(unitHolder?.closing_balance || 0).toFixed(4),
            )}
          </div>
        </div>
      ))}
    </>
  );
};

interface RegularStatementTableProps {
  unitHolderStatement: any[];
  selectedValues: any;
  CapTxnType: (type: string) => string;
  dailyNav: any[];
  itemsToShow: number;
}

export const RegularStatementTable: React.FC<RegularStatementTableProps> = ({
  unitHolderStatement,
  selectedValues,
  CapTxnType,
  dailyNav,
  itemsToShow,
}) => {
  return (
    <>
      <table
        className="report"
        style={{
          color: "black",
          width: "100%",
          borderCollapse: "collapse",
          tableLayout: "fixed",
          marginTop: 0,
        }}
      >
        <thead>
          <tr
            style={{
              borderTop: "2px solid black",
              borderBottom: "2px solid black",
            }}
          >
            {selectedValues?.columns?.includes("Date") && (
              <th
                className="htmltoPdfStyle"
                style={{ width: "6%", textAlign: "left", padding: "8px 4px" }}
              >
                Date
              </th>
            )}
            {selectedValues?.columns?.includes("Description") && (
              <th
                className="htmltoPdfStyle"
                style={{ width: "17%", textAlign: "left", padding: "8px 4px" }}
              >
                Description
              </th>
            )}
            {selectedValues?.columns?.includes("Fund Units") && (
              <th
                className="htmltoPdfStyle"
                style={{ width: "8%", textAlign: "right", padding: "8px 4px" }}
              >
                Units
              </th>
            )}
            {selectedValues?.columns?.includes("NAV") && (
              <th
                className="htmltoPdfStyle"
                style={{ width: "5%", textAlign: "right", padding: "8px 4px" }}
              >
                NAV
              </th>
            )}
            {selectedValues?.columns?.includes("Gross Amount") && (
              <th
                className="htmltoPdfStyle"
                style={{ width: "9%", textAlign: "right", padding: "8px 4px" }}
              >
                Gross Amount
              </th>
            )}
            {selectedValues?.columns?.includes("Load") && (
              <th
                className="htmltoPdfStyle"
                style={{ width: "11%", textAlign: "right", padding: "8px 4px" }}
              >
                Load
              </th>
            )}
            {selectedValues?.columns?.includes("Tax") && (
              <th
                className="htmltoPdfStyle"
                style={{ width: "8%", textAlign: "right", padding: "8px 4px" }}
              >
                Tax
              </th>
            )}
            {selectedValues?.columns?.includes("Net Amount") && (
              <th
                className="htmltoPdfStyle"
                style={{ width: "9%", textAlign: "right", padding: "8px 4px" }}
              >
                Net Amount
              </th>
            )}
            {selectedValues?.columns?.includes("Closing Units") && (
              <th
                className="htmltoPdfStyle"
                style={{ width: "13%", textAlign: "right", padding: "8px 4px" }}
              >
                Balance Units
              </th>
            )}
          </tr>
        </thead>
      </table>

      {unitHolderStatement
        ?.slice(0, itemsToShow)
        .map((unitHolder: any, index: number) => (
          <React.Fragment key={index}>
            <div
              style={{
                pageBreakInside: "avoid",
                breakInside: "avoid",
                display: "block",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  pageBreakInside: "avoid",
                  breakInside: "avoid",
                  pageBreakAfter: "avoid",
                  breakAfter: "avoid",
                  width: "100%",
                  marginBottom: "2px",
                }}
              >
                <div style={{ fontSize: "12px", fontWeight: "bold" }}>
                  {unitHolder?.fund_name
                    ? `${unitHolder?.fund_name} (${unitHolder.fund_code})`
                    : unitHolder?.fund_code}
                </div>

                {selectedValues?.balance?.includes("Opening Balance") && (
                  <div className="d-flex pr-1" style={{ fontSize: "12px" }}>
                    <b style={{ marginRight: "2px" }}>Opening Units:</b>
                    <CardColComponent
                      subTitle={numberWithCommas(
                        parseFloat(unitHolder.openingUnits || 0).toFixed(4),
                      )}
                      amount={true}
                      simple={true}
                    />
                  </div>
                )}
              </div>

              <table
                className="report"
                style={{
                  color: "black",
                  width: "100%",
                  borderCollapse: "collapse",
                  marginTop: 0,
                  tableLayout: "fixed",
                }}
              >
                {unitHolder?.forPeriodTxns?.length > 0 ? (
                  <>
                    <tbody className="report-table-body">
                      {unitHolder.forPeriodTxns.map(
                        (item: any, idx: number) => (
                          <tr
                            key={idx}
                            style={{
                              alignSelf: "center",
                            }}
                          >
                            {selectedValues?.columns?.includes("Date") && (
                              <td
                                style={{
                                  ...getStyles({ width: "6%" }),
                                }}
                              >
                                <span className="htmltoPdfStyle">
                                  {moment(item?.settlement_date)
                                    .format("DD-MMM-YY")
                                    ?.replaceAll("-", " ")}
                                </span>
                              </td>
                            )}
                            {selectedValues?.columns?.includes(
                              "Description",
                            ) && (
                              <td
                                style={{
                                  ...getStyles({ width: "17%" }),
                                }}
                              >
                                <div className="d-flex flex-column">
                                  <span>
                                    {CapTxnType(item?.txnr_type) !==
                                    "Transfer of Units"
                                      ? CapTxnType(item?.txnr_type)
                                      : item?.broker === "receiver"
                                        ? CapTxnType(item?.txnr_type) +
                                          `(receiver)`
                                        : CapTxnType(item?.txnr_type) +
                                          `(sender)`}
                                  </span>
                                  <small>#{item?.txn_id}</small>
                                </div>
                              </td>
                            )}
                            {selectedValues?.columns?.includes(
                              "Fund Units",
                            ) && (
                              <td
                                className="text-right"
                                style={{
                                  ...getStyles({
                                    width: "8%",
                                    textAlign: "right",
                                  }),
                                }}
                              >
                                <CardColComponent
                                  subTitle={numberWithCommas(
                                    parseFloat(item?.units).toFixed(4),
                                  )}
                                  amount={true}
                                  simple={true}
                                />
                              </td>
                            )}
                            {selectedValues?.columns?.includes("NAV") && (
                              <td
                                className="text-right"
                                style={{
                                  ...getStyles({
                                    width: "5%",
                                    textAlign: "right",
                                  }),
                                }}
                              >
                                <CardColComponent
                                  subTitle={parseFloat(item?.nav || 0).toFixed(
                                    4,
                                  )}
                                  amount={true}
                                  simple={true}
                                />
                              </td>
                            )}
                            {selectedValues?.columns?.includes(
                              "Gross Amount",
                            ) && (
                              <td
                                className="text-right"
                                style={{
                                  ...getStyles({
                                    width: "9%",
                                    textAlign: "right",
                                  }),
                                }}
                              >
                                <CardColComponent
                                  subTitle={numberWithCommas(
                                    parseFloat(item?.gross_amount).toFixed(4),
                                  )}
                                  amount={true}
                                  simple={true}
                                />
                              </td>
                            )}
                            {selectedValues?.columns?.includes("Load") && (
                              <td
                                className="text-right"
                                style={{
                                  ...getStyles({
                                    width: "11%",
                                    textAlign: "right",
                                  }),
                                }}
                              >
                                <CardColComponent
                                  subTitle={numberWithCommas(
                                    parseFloat(item?.load || 0).toFixed(4),
                                  )}
                                  amount={true}
                                  simple={true}
                                />
                              </td>
                            )}
                            {selectedValues?.columns?.includes("Tax") && (
                              <td
                                className="text-right"
                                style={{
                                  ...getStyles({
                                    width: "8%",
                                    textAlign: "right",
                                  }),
                                }}
                              >
                                <CardColComponent
                                  subTitle={numberWithCommas(
                                    parseFloat(item?.tax || 0).toFixed(4),
                                  )}
                                  amount={true}
                                  simple={true}
                                />
                              </td>
                            )}
                            {selectedValues?.columns?.includes(
                              "Net Amount",
                            ) && (
                              <td
                                className="text-right"
                                style={{
                                  ...getStyles({
                                    width: "9%",
                                    textAlign: "right",
                                  }),
                                }}
                              >
                                <CardColComponent
                                  subTitle={numberWithCommas(
                                    parseFloat(item?.net_amount).toFixed(4),
                                  )}
                                  amount={true}
                                  simple={true}
                                />
                              </td>
                            )}
                            {selectedValues?.columns?.includes(
                              "Closing Units",
                            ) && (
                              <td
                                className="text-right"
                                style={{
                                  ...getStyles({
                                    width: "13%",
                                    textAlign: "right",
                                  }),
                                }}
                              >
                                <CardColComponent
                                  subTitle={numberWithCommas(
                                    parseFloat(item?.closingUnits).toFixed(4),
                                  )}
                                  amount={true}
                                  simple={true}
                                />
                              </td>
                            )}
                          </tr>
                        ),
                      )}
                    </tbody>
                    <tfoot>
                      <tr key={index}>
                        {selectedValues?.columns?.includes("Date") && (
                          <th
                            className="htmltoPdfStyle"
                            id="test"
                            style={{
                              width: "6%",
                              textAlign: "left",
                              padding: "8px 4px",

                              fontWeight: "bold",
                            }}
                          >
                            {selectedValues?.columns?.filter(
                              (ele) =>
                                ele == "Fund Units" ||
                                ele == "Net Amount" ||
                                ele == "Gross Amount" ||
                                ele == "Load" ||
                                ele == "Tax",
                            )?.length > 0
                              ? "Total"
                              : ""}
                          </th>
                        )}
                        {/* {selectedValues?.columns?.includes("TXN ID") && (
                                                            <th
                                                              id="test"
                                                              style={{
                                                                width: "8%",
                                                                padding: "8px 4px",
                                                              }}
                                                            ></th>
                                                          )} */}
                        {selectedValues?.columns?.includes("Description") && (
                          <th
                            id="test"
                            style={{
                              width: "17%",
                              padding: "8px 4px",
                            }}
                          ></th>
                        )}
                        {selectedValues?.columns?.includes("Fund Units") && (
                          <th
                            className="text-right"
                            style={{
                              width: "8%",
                              textAlign: "right",
                              padding: "8px 4px",
                              fontWeight: "bold",
                            }}
                          >
                            <CardColComponent
                              subTitle={numberWithCommas(
                                parseFloat(unitHolder.total_units).toFixed(4),
                              )}
                              amount={true}
                              simple={true}
                            />
                          </th>
                        )}
                        {selectedValues?.columns?.includes("NAV") && (
                          <th
                            id="test"
                            style={{
                              width: "5%",
                              padding: "8px 4px",
                            }}
                          ></th>
                        )}
                        {selectedValues?.columns?.includes("Gross Amount") && (
                          <th
                            className="text-right"
                            style={{
                              width: "9%",
                              textAlign: "right",
                              padding: "8px 4px",
                              fontWeight: "bold",
                            }}
                          >
                            <CardColComponent
                              subTitle={numberWithCommas(
                                parseFloat(
                                  unitHolder.total_grossAmount,
                                ).toFixed(4),
                              )}
                              amount={true}
                              simple={true}
                            />
                          </th>
                        )}

                        {selectedValues?.columns?.includes("Load") && (
                          <th
                            className="text-right"
                            style={{
                              width: "11%",
                              textAlign: "right",
                              padding: "8px 4px",
                              fontWeight: "bold",
                            }}
                          >
                            <CardColComponent
                              subTitle={numberWithCommas(
                                parseFloat(unitHolder?.total_load || 0).toFixed(
                                  2,
                                ),
                              )}
                              amount={true}
                              simple={true}
                            />
                          </th>
                        )}
                        {selectedValues?.columns?.includes("Tax") && (
                          <th
                            className="text-right"
                            style={{
                              width: "8%",
                              textAlign: "right",
                              padding: "8px 4px",
                              fontWeight: "bold",
                            }}
                          >
                            <CardColComponent
                              subTitle={numberWithCommas(
                                parseFloat(unitHolder.total_CGT).toFixed(4),
                              )}
                              amount={true}
                              simple={true}
                            />
                          </th>
                        )}
                        {selectedValues?.columns?.includes("Net Amount") && (
                          <th
                            className="text-right"
                            style={{
                              width: "9%",
                              textAlign: "right",
                              padding: "8px 4px",
                              fontWeight: "bold",
                            }}
                          >
                            <CardColComponent
                              subTitle={numberWithCommas(
                                parseFloat(unitHolder.total_netAmount).toFixed(
                                  2,
                                ),
                              )}
                              amount={true}
                              simple={true}
                            />
                          </th>
                        )}
                        {selectedValues?.columns?.includes("Closing Units") && (
                          <th
                            id="test"
                            style={{
                              width: "13%",
                              padding: "8px 4px",
                            }}
                          ></th>
                        )}
                      </tr>
                    </tfoot>
                  </>
                ) : (
                  <div className="d-flex justify-content-center py-2">
                    <b className="text-center">No Recent Transactions!</b>
                  </div>
                )}
              </table>
            </div>
            <div className="flex flex-column align-items-end justify-content-end">
              {selectedValues?.balance?.includes("Closing Balance") && (
                <div
                  id="test"
                  className="d-flex float-right closingBalancePDF"
                  style={{
                    marginTop: "-20px",
                    fontSize: "12px",
                  }}
                >
                  <b
                    style={{
                      marginRight: "2px",
                    }}
                  >
                    Closing Units:
                  </b>
                  <CardColComponent
                    subTitle={numberWithCommas(
                      parseFloat(unitHolder.closingUnits).toFixed(4),
                    )}
                    amount={true}
                    simple={true}
                  />
                </div>
              )}
              <br id="test" />
              {dailyNav?.find((item) => item.fund == unitHolder.fund_code)
                ?.nav > 0 &&
                selectedValues?.columns?.includes("NAV") && (
                  <div
                    id="test"
                    className="d-flex float-right"
                    style={{
                      marginTop: "-20px",
                      fontSize: "12px",
                    }}
                  >
                    <b
                      style={{
                        marginRight: "2px",
                      }}
                    >
                      NAV
                    </b>{" "}
                    :
                    <CardColComponent
                      subTitle={numberWithCommas(
                        (unitHolder?.closingNavRecord?.nav || 0)?.toFixed(4),
                      )}
                      amount={true}
                      simple={true}
                    />
                  </div>
                )}
              <br id="test" />
              {dailyNav?.find((item) => item.fund == unitHolder.fund_code)
                ?.nav > 0 && (
                <div
                  id="test"
                  className="d-flex float-right"
                  style={{
                    marginTop: "-20px",
                    fontSize: "12px",
                  }}
                >
                  <b id="test">Closing Balance</b> :
                  <CardColComponent
                    subTitle={numberWithCommas(
                      (unitHolder?.closingBalance || 0)?.toFixed(4),
                    )}
                    amount={true}
                    simple={true}
                  />
                </div>
              )}
              <br id="test" />
            </div>
          </React.Fragment>
        ))}
    </>
  );
};